import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface MarketingSyncRun {
  id: number;
  mode: string | null;
  status: string | null;
  started_at: string | null;
  finished_at: string | null;
  rows_upserted: number | null;
  watermark_from: string | null;
  watermark_to: string | null;
  source_count: number | null;
  mirror_count: number | null;
  error: string | null;
}

export interface MarketingMirrorStats {
  total: number;
  qproCount: number;
  lastSyncedAt: string | null;
}

export interface MarketingMirrorLead {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  job_title: string | null;
  campaign_key: string | null;
  status: string | null;
  heat_level: string | null;
  updated_at: string | null;
  synced_at: string | null;
}

const TABLE_MISSING_CODES = new Set(['42P01', 'PGRST205']);

const isTableMissingError = (error: { code?: string; message?: string }): boolean => {
  if (error.code && TABLE_MISSING_CODES.has(error.code)) return true;
  if (error.message && error.message.toLowerCase().includes('relation') && error.message.toLowerCase().includes('does not exist')) return true;
  return false;
};

// ─────────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────────

export const useMarketingSyncRuns = () => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<MarketingSyncRun[], Error, MarketingSyncRun[], readonly unknown[]>({
    // Tenancy sweep: identity in the key, explicit predicate in the query, AND-composed gate.
    // These tables are all `organization_id NOT NULL`, so the predicate cannot drop a row — it is
    // defence in depth today and a real boundary the moment a second organization exists. RLS bounds
    // the rows; it does not partition a browser cache across an identity switch in one session.
    queryKey: ['marketing-sync-runs', userId, organizationId],
    queryFn: async () => {
      if (organizationId === null) return [];
      const { data, error } = await supabase
        .from('mktg_sync_runs')
        .select('id, mode, status, started_at, finished_at, rows_upserted, watermark_from, watermark_to, source_count, mirror_count, error')
        .eq('organization_id', organizationId)
        .order('id', { ascending: false })
        .limit(10);

      if (error) throw error;
      return (data ?? []) as MarketingSyncRun[];
    },
    retry: (failureCount, error) => {
      if (isTableMissingError(error as { code?: string; message?: string })) return false;
      return failureCount < 1;
    },
    staleTime: 30000,
  });
};

export const useMarketingMirrorStats = () => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<MarketingMirrorStats, Error, MarketingMirrorStats, readonly unknown[]>({
    // Tenancy sweep: identity in the key, explicit predicate in the query, AND-composed gate.
    // These tables are all `organization_id NOT NULL`, so the predicate cannot drop a row — it is
    // defence in depth today and a real boundary the moment a second organization exists. RLS bounds
    // the rows; it does not partition a browser cache across an identity switch in one session.
    queryKey: ['marketing-mirror-stats', userId, organizationId],
    queryFn: async () => {
      const [countResult, qproResult, lastSyncResult] = await Promise.all([
        supabase
          .from('mktg_mirror_leads')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', organizationId ?? ''),
        supabase
          .from('mktg_mirror_leads')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', organizationId ?? '')
          .eq('campaign_key', 'qpro-outsourcing'),
        supabase
          .from('mktg_mirror_leads')
          .select('synced_at')
          .eq('organization_id', organizationId ?? '')
          .order('synced_at', { ascending: false })
          .limit(1)
          .single(),
      ]);

      // Any of the three can expose the missing-table error
      const firstError = countResult.error ?? qproResult.error;
      if (firstError) throw firstError;

      // .single() returns PGRST116 when the mirror is empty — that is not an error here
      const resolveLastSynced = (): string | null => {
        if (lastSyncResult.error) {
          if (lastSyncResult.error.code === 'PGRST116') return null;
          throw lastSyncResult.error;
        }
        return lastSyncResult.data?.synced_at ?? null;
      };

      return {
        total: countResult.count ?? 0,
        qproCount: qproResult.count ?? 0,
        lastSyncedAt: resolveLastSynced(),
      };
    },
    retry: (failureCount, error) => {
      if (isTableMissingError(error as { code?: string; message?: string })) return false;
      return failureCount < 1;
    },
    staleTime: 30000,
  });
};

export const useMarketingMirrorLeads = () => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<MarketingMirrorLead[], Error, MarketingMirrorLead[], readonly unknown[]>({
    queryKey: ['marketing-mirror-leads', userId, organizationId],
    queryFn: async () => {
      if (organizationId === null) return [];
      const { data, error } = await supabase
        .from('mktg_mirror_leads')
        .select('id, first_name, last_name, company_name, job_title, campaign_key, status, heat_level, updated_at, synced_at')
        .eq('organization_id', organizationId)
        .order('updated_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      return (data ?? []) as MarketingMirrorLead[];
    },
    retry: (failureCount, error) => {
      if (isTableMissingError(error as { code?: string; message?: string })) return false;
      return failureCount < 1;
    },
    staleTime: 30000,
  });
};
